import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PartType } from '@core/types';
import CampoNumero from './CampoNumero';
import { acharLugares } from '../lib/buscaDoMapa';
import { plantaDaLoja } from '../lib/consultas';
import { lugaresComEndereco } from '../lib/enderecos';
import { PLANTA_INICIAL, lerPlanta, objetosDaPlanta } from '../lib/loja3d';
import type { RascunhoDoBalcao } from '../lib/cadastroDoBalcao';

// =============================================================================
// O formulário do cadastro pelo balcão (093, PR 2) — os MESMOS campos no
// cadastrar do colaborador e no "Corrigir" da gerência, campos em coluna para
// o celular. Sem custo, margem, fornecedor nem similares: isso é o cadastro
// completo, da gerência, depois.
//
// iPhone: nada de <datalist> (o Safari não o mostra). O tipo é <select> e o
// lugar sugere em BOTÕES — o mesmo motivo.
// =============================================================================

export default function FormularioDoBalcao({
  valores, aoMudar, tipos, tipoSugerido = null, focarNome = false,
}: {
  valores: RascunhoDoBalcao;
  aoMudar: (r: RascunhoDoBalcao) => void;
  tipos: ReadonlyArray<PartType>;
  /** O tipo pelo começo do nome (sugerirTipo), quando a pessoa não escolheu. */
  tipoSugerido?: PartType | null;
  focarNome?: boolean;
}) {
  const id = useId();
  const set = <K extends keyof RascunhoDoBalcao>(campo: K, v: RascunhoDoBalcao[K]) =>
    aoMudar({ ...valores, [campo]: v });

  // A mesma cadeia do "Mudar de lugar" (MudarDeLugar.tsx): a planta salva,
  // senão a do arquivo. Aqui sem a contagem de peças por lugar — o que se quer
  // é só o CÓDIGO do lugar, e um toque o preenche.
  const planta = useQuery({ queryKey: ['planta-da-loja'], queryFn: plantaDaLoja, staleTime: 5 * 60_000 });
  const { objetos, lugares } = useMemo(() => {
    let objs;
    try {
      objs = objetosDaPlanta(planta.data ? lerPlanta(planta.data.planta) : PLANTA_INICIAL);
    } catch {
      objs = objetosDaPlanta(PLANTA_INICIAL);
    }
    return { objetos: objs, lugares: lugaresComEndereco(objs, new Map()) };
  }, [planta.data]);
  const sugestoes = useMemo(() => {
    const achados = acharLugares(valores.local, lugares, objetos);
    const digitado = valores.local.replace(/\s/g, '').toUpperCase();
    // Já escolhido (ou digitado exato): não fica oferecendo o mesmo.
    return achados.filter((l) => l.codigo.toUpperCase() !== digitado).slice(0, 5);
  }, [valores.local, lugares, objetos]);

  const tipoNaTela = valores.tipo ?? tipoSugerido?.slug ?? '';

  return (
    <div className="space-y-4">
      <div>
        <label className="rotulo" htmlFor={`${id}-nome`}>Nome da peça *</label>
        <input id={`${id}-nome`} className="campo" value={valores.descricao} autoComplete="off"
               autoFocus={focarNome} placeholder="Pastilha de freio dianteira"
               onChange={(e) => set('descricao', e.target.value)} />
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-codigo`}>Código (fabricante, OEM ou barras)</label>
        {/* O leitor USB bipa AQUI: ele digita o código e manda Enter — e o
            Enter não cadastra (a trava está na tela). */}
        <input id={`${id}-codigo`} className="campo font-mono" value={valores.codigo} autoComplete="off"
               placeholder="Pode bipar o código de barras"
               onChange={(e) => set('codigo', e.target.value)} />
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-marca`}>Marca</label>
        <input id={`${id}-marca`} className="campo" value={valores.marca} autoComplete="off"
               placeholder="Fras-le" onChange={(e) => set('marca', e.target.value)} />
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-tipo`}>Tipo</label>
        <select id={`${id}-tipo`} className="campo" value={tipoNaTela}
                onChange={(e) => set('tipo', e.target.value)}>
          <option value="">Sem tipo</option>
          {tipos.map((t) => <option key={t.slug} value={t.slug}>{t.categoria} · {t.nome}</option>)}
        </select>
        {valores.tipo === null && tipoSugerido && (
          <p className="mt-1 text-xs text-dim">Escolhido pelo nome — troque se não for.</p>
        )}
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-preco`}>Preço de balcão (R$) *</label>
        <CampoNumero id={`${id}-preco`} className="campo" valor={valores.preco} placeholder="0,00"
                     onValor={(n) => set('preco', n)} />
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-local`}>Onde fica (opcional)</label>
        <input id={`${id}-local`} className="campo font-mono" value={valores.local} autoComplete="off"
               placeholder="Código ou nome do lugar" onChange={(e) => set('local', e.target.value)} />
        {sugestoes.length > 0 && (
          <ul className="mt-2 space-y-1" aria-label="Lugares da loja que combinam">
            {sugestoes.map((l) => (
              <li key={l.codigo}>
                <button type="button" onClick={() => set('local', l.codigo)}
                        className="flex min-h-11 w-full items-center gap-2 border border-edge px-3 text-left text-sm hover:border-signal">
                  <span className="font-mono font-bold">{l.codigo}</span>
                  <span className="min-w-0 truncate text-xs text-mute">{l.nome || l.descricao}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label className="rotulo" htmlFor={`${id}-qtd`}>Quantas ficam na prateleira</label>
        <CampoNumero id={`${id}-qtd`} className="campo" valor={valores.quantidade} placeholder="0"
                     onValor={(n) => set('quantidade', n)} />
        <p className="mt-1 text-xs text-dim">
          Se vai levar uma agora pela peça diversa, não conte essa.
        </p>
      </div>
    </div>
  );
}
